import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import defaultOrgAvatar from '../../../assets/images/CompanyDefaultLogo.svg';
import { selectUserId } from '../../../selectors/authSelectors';

import { CURRENCIES } from '../../../constants/currenciesConstants';

import styles from './TrackerCard.module.scss';

const ARCHIVED_STATUSES = [
  'hired',
  'hired_and_archived',
  'declined',
  'passed',
  'not_your_refer',
  'expired',
  'archived',
];

const getStatusTagConfig = (t) => ({
  new: { label: t('tracker.statusNew', 'New'), className: 'statusNew' },
  applied: { label: t('tracker.applied', 'Applied'), className: 'statusApplied' },
  interview: { label: t('tracker.interviewing', 'Interviewing'), className: 'statusInterview' },
  offer: { label: t('tracker.statusOffer', 'Offer'), className: 'statusOffer' },
  hired: { label: `✓ ${t('tracker.statusHired', 'Hired')}`, className: 'statusHired' },
  hired_and_archived: { label: `✓ ${t('tracker.statusHired', 'Hired')}`, className: 'statusHired' },
  passed: { label: t('tracker.statusPassed', 'Passed'), className: 'statusPassed' },
  declined: { label: t('tracker.statusDeclined', 'Declined'), className: 'statusDeclined' },
  expired: { label: t('tracker.statusExpired', 'Expired'), className: 'statusExpired' },
  not_your_refer: { label: t('tracker.statusReferralDeclined', 'Referral Declined'), className: 'statusDeclined' },
  archived: { label: `✗ ${t('tracker.statusRejected', 'Rejected')}`, className: 'statusRejected' },
});

const resolveInterviewStageLabel = (job, candidateStatus) => {
  const stages = job?.interview_stages;
  if (!stages || !Array.isArray(stages)) return null;
  const match = stages.find((s) => s.value === candidateStatus);
  return match ? match.label : null;
};

/** Extract initials from a full name (e.g. "Usama Bashir" → "UB") */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

/** Format reward amount with correct currency symbol */
const formatReward = (rewards) => {
  if (!rewards?.monetary) return null;
  const symbol = CURRENCIES[rewards.currency_names] || '$';
  return `${symbol}${Number(rewards.monetary).toLocaleString()}`;
};

/** Sum payout amounts grouped by currency */
const formatPayoutTotal = (payouts) => {
  if (!payouts?.length) return null;
  const monetary = payouts.filter((p) => p.reward_amount != null);
  if (!monetary.length) return null;

  const byCurrency = {};
  monetary.forEach((p) => {
    const sym = p.currency_symbol || '$';
    byCurrency[sym] = (byCurrency[sym] || 0) + (p.reward_amount || 0);
  });

  return Object.entries(byCurrency)
    .map(([sym, total]) => `${sym}${total.toLocaleString()}`)
    .join(' + ');
};

const TrackerCard = ({ job, direction, status }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const currentUserId = useSelector(selectUserId);

  const firstReferral = job.referrals?.[0];
  if (!firstReferral) return null;

  const statusTagConfig = getStatusTagConfig(t);
  const candidateStatus = firstReferral.candidate_status || status;
  const isArchived = ARCHIVED_STATUSES.includes(candidateStatus);
  const image = job.logo_image_url || defaultOrgAvatar;
  const referralsCount = (job.referrals?.length || 1) - 1;
  const isDirectApplication = firstReferral.id === currentUserId;
  const showIneligible = !firstReferral.reward_eligible && status !== 'new';

  const getStatusTag = () => {
    if (status === 'interview') {
      const stageLabel = resolveInterviewStageLabel(job, candidateStatus);
      if (stageLabel) return { label: stageLabel, className: 'statusInterview' };
      return statusTagConfig.interview;
    }
    if (isArchived) {
      return statusTagConfig[candidateStatus] || statusTagConfig.archived;
    }
    if (candidateStatus.includes('interview')) {
      const stageLabel = resolveInterviewStageLabel(job, candidateStatus);
      if (stageLabel) return { label: stageLabel, className: 'statusInterview' };
      return statusTagConfig.interview;
    }
    return null;
  };

  const statusTag = getStatusTag();
  const payoutTotal = direction === 'their' ? formatPayoutTotal(firstReferral.payouts) : null;
  const rewardDisplay = payoutTotal || formatReward(job.rewards);

  const handleClick = () => {
    const params = new URLSearchParams({
      jobId: job.uuid || job.id,
      referralId: firstReferral.uuid || firstReferral.id || '',
      requestId: firstReferral.request_uuid || firstReferral.request_id || '',
      direction,
      status: candidateStatus,
    });
    navigate(`/tracker/details?${params.toString()}`);
  };

  const referrerName = firstReferral.full_name || firstReferral.short_name || '';
  const referrerInitials = getInitials(referrerName);

  // ── My Jobs card ──────────────────────────────────────────
  if (direction === 'my') {
    return (
      <div
        className={styles.card}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      >
        {showIneligible && <span className={styles.ineligibleBadge}>{t('tracker.ineligible', 'Ineligible')}</span>}

        <div className={styles.cardBody}>
          <div className={styles.cardTop}>
            <img
              src={image}
              alt=""
              className={styles.companyLogo}
              onError={(e) => {
                e.target.src = defaultOrgAvatar;
              }}
            />
            <div className={styles.cardInfo}>
              <span className={styles.jobTitle}>{job.title}</span>
              <span className={styles.companyName}>{job.organization_name}</span>
            </div>
          </div>

          {/* Referrer: initials circle + name, or Direct Application badge */}
          <div className={styles.referrerRow}>
            {isDirectApplication ? (
              <span className={styles.directApplicationBadge}>
                {t('tracker.directApplication', 'Direct Application')}
              </span>
            ) : (
              <>
                <div className={styles.initialsCircle}>
                  <span className={styles.initialsText}>{referrerInitials}</span>
                </div>
                <span className={styles.referrerName}>{referrerName}</span>
                {referralsCount > 0 && <span className={styles.moreReferrers}>+{referralsCount}</span>}
              </>
            )}
          </div>

          {statusTag && (
            <div className={styles.statusRow}>
              <span className={classNames(styles.statusBadge, styles[statusTag.className])}>{statusTag.label}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Sent Jobs card (direction === 'their') ────────────────
  const candidateName = firstReferral.full_name || '';
  const candidateInitials = getInitials(candidateName);

  return (
    <div
      className={styles.card}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {showIneligible && <span className={styles.ineligibleBadge}>{t('tracker.ineligible', 'Ineligible')}</span>}

      <div className={styles.cardBody}>
        <div className={styles.cardTop}>
          <img
            src={image}
            alt=""
            className={styles.companyLogo}
            onError={(e) => {
              e.target.src = defaultOrgAvatar;
            }}
          />
          <div className={styles.cardInfo}>
            <span className={styles.candidateName}>{candidateName}</span>
            <span className={styles.jobTitle}>{job.title}</span>
            <span className={styles.companyName}>{job.organization_name}</span>
          </div>
        </div>

        {rewardDisplay ? (
          <div className={styles.bountyRow}>
            <span className={classNames(styles.bounty, { [styles.bountyHired]: candidateStatus === 'hired' })}>
              {rewardDisplay}
            </span>
          </div>
        ) : null}

        {statusTag && (
          <div className={styles.statusRow}>
            <span className={classNames(styles.statusBadge, styles[statusTag.className])}>{statusTag.label}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackerCard;
